import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ActionsFlagHighlightedXsmall from '@strava/icons/ActionsFlagHighlightedXsmall';
import { Markup } from 'interweave';
import Button from '@strava/ui/Button';
import { useFeedEntityContext } from '../../../../state/FeedEntityContext';

import styles from '../index.scss';

const RED = '#FC4C01';
const ELLIPSIS = '•••';

const ActivityTextBody = ({
  activityId,
  activityName,
  ownedByCurrentAthlete,
  flagged,
  description
}) => {
  const targetRef = useRef();

  const [readMore, setReadMore] = useState(false);
  const { entityState } = useFeedEntityContext();
  const toggleReadMore = () => {
    setReadMore(!readMore);
  };

  const [height, setHeight] = useState(0);
  const [descriptionHeight, setDescriptionHeight] = useState(0);
  useEffect(() => {
    if (targetRef.current) {
      setHeight(targetRef.current.firstElementChild.scrollHeight);
      setDescriptionHeight(
        targetRef.current.firstElementChild.firstElementChild.offsetHeight
      );
    }
  }, []);

  const lineLimit = 74;
  const getWrapperStyle = (shouldTruncate) => {
    if (readMore) {
      return ['expanded', { height: `${height}px` }];
    }
    // Limit the text at 4 lines
    if (shouldTruncate) {
      return ['truncated', { height: `${lineLimit}px` }];
    }
    return ['', {}];
  };

  const isExpandable =
    descriptionHeight > lineLimit || (description && description.length > 350);

  return (
    <>
      {/* Title Text */}
      <h3 className={`text-title-1 ${styles.activityName}`}>
        <a
          href={`/activities/${activityId}`}
          className={styles.activityName}
          data-testid="activity_name"
          onClick={() => {
            entityState.trackAnalytics({
              action: 'click',
              properties: { element_source: 'title' }
            });
          }}
        >
          {activityName}
        </a>
        {ownedByCurrentAthlete && flagged && (
          <span className={styles.iconWrapper}>
            <ActionsFlagHighlightedXsmall
              fill={RED}
              data-testid="activity_flag"
            />
          </span>
        )}
      </h3>

      {/* Activity Description */}
      {description && (
        <div className={styles.descriptionBody} ref={targetRef}>
          <div
            className={`${styles.wrapper} ${getWrapperStyle(isExpandable)[0]}`}
            data-testid="activity_description_wrapper"
            style={getWrapperStyle(isExpandable)[1]}
          >
            <a
              href={`/activities/${activityId}`}
              data-testid="activity_description"
            >
              <Markup noWrap={true} content={description.trim()} />
            </a>
          </div>
          {isExpandable && (
            <div className={styles.descriptionReadMore}>
              <Button
                data-testid="activity_description_read_more"
                onClick={toggleReadMore}
                onKeyDown={toggleReadMore}
                tabIndex={0}
                type="button"
              >
                {ELLIPSIS}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

ActivityTextBody.propTypes = {
  activityId: PropTypes.string.isRequired,
  activityName: PropTypes.string.isRequired,
  ownedByCurrentAthlete: PropTypes.bool,
  flagged: PropTypes.bool,
  description: PropTypes.string
};

ActivityTextBody.defaultProps = {
  ownedByCurrentAthlete: false,
  flagged: false,
  description: PropTypes.string
};

export default ActivityTextBody;
